/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter } from 'react-router';
import { AuthGuard } from './components/AuthGuard';
import { DashboardLayout } from './layouts/DashboardLayout';

function DashboardHome() {
  const { t } = useTranslation('common');
  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-4">{t('dashboardPage.title')}</h1>
      <p className="text-muted-foreground">{t('dashboardPage.welcome')}</p>
    </div>
  );
}

// Eagerly loaded — always needed for auth flow
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';

// Lazy loaded — infrequently accessed pages
const ForgotPasswordPage = lazy(() =>
  import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./pages/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const OnboardingPage = lazy(() =>
  import('./pages/onboarding/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const BookkeepingPage = lazy(() =>
  import('./pages/bookkeeping/BookkeepingPage').then((m) => ({ default: m.BookkeepingPage })),
);
const ExpensesPage = lazy(() =>
  import('./pages/bookkeeping/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);

// Dashboard pages (protected)
import { ChatPage } from './pages/chat/ChatPage';
import { ClientDetailPage } from './pages/clients/ClientDetailPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { InvoiceDetailPage } from './pages/invoices/InvoiceDetailPage';
import { InvoiceFormPage } from './pages/invoices/InvoiceFormPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import SettingsPage from './pages/settings/SettingsPage';
import { BillingPage } from './pages/billing/BillingPage';
import { SubscriptionInvoicePage } from './pages/billing/SubscriptionInvoicePage';

const lazyFallback = (
  <div className="flex h-screen items-center justify-center bg-surface-50">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

export const router = createBrowserRouter([
  // Public auth routes
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  {
    path: '/forgot-password',
    element: <Suspense fallback={lazyFallback}><ForgotPasswordPage /></Suspense>,
  },
  {
    path: '/reset-password',
    element: <Suspense fallback={lazyFallback}><ResetPasswordPage /></Suspense>,
  },
  {
    path: '/verify-email',
    element: <Suspense fallback={lazyFallback}><VerifyEmailPage /></Suspense>,
  },

  // Protected routes
  {
    element: <AuthGuard />,
    children: [
      {
        path: '/onboarding',
        element: <Suspense fallback={lazyFallback}><OnboardingPage /></Suspense>,
      },

      // Dashboard (with sidebar layout)
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <DashboardHome />,
          },
          { path: 'chat', element: <ChatPage /> },
          { path: 'clients', element: <ClientsPage /> },
          { path: 'clients/:id', element: <ClientDetailPage /> },
          {
            path: 'bookkeeping',
            element: <Suspense fallback={lazyFallback}><BookkeepingPage /></Suspense>,
          },
          {
            path: 'bookkeeping/expenses',
            element: <Suspense fallback={lazyFallback}><ExpensesPage /></Suspense>,
          },
          { path: 'invoices', element: <InvoicesPage /> },
          { path: 'invoices/new', element: <InvoiceFormPage /> },
          { path: 'invoices/:id', element: <InvoiceDetailPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'billing/invoices/:id', element: <SubscriptionInvoicePage /> },
        ],
      },
    ],
  },
]);
